import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useT } from "../../i18n/LanguageContext";
import LanguageToggle from "./LanguageToggle";

export default function Header() {
  const { t } = useT();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: isHome ? "#galeria" : "/#galeria", label: t.nav.gallery, key: "galeria" },
    { href: isHome ? "#comodidades" : "/#comodidades", label: t.nav.amenities, key: "comodidades" },
    { href: isHome ? "#vizinhanca" : "/#vizinhanca", label: t.nav.neighborhood, key: "vizinhanca" },
    { href: isHome ? "#reserva" : "/#reserva", label: t.nav.booking, key: "reserva" },
    { to: "/sobre", label: t.nav.about, key: "sobre" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 transition-colors duration-300 ${
        scrolled ? "bg-[#0e1a3f]/95 backdrop-blur-md shadow-lg" : "bg-[#0e1a3f]"
      }`}
    >
      <div className="azulejo-band" aria-hidden="true" />
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 gap-3">
        {/* Logo — compact on mobile */}
        <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="brand-logo">
          <img
            src="/assets/logo.jpg"
            alt="MexiHome5"
            className="w-10 h-10 sm:w-14 sm:h-14 rounded-full object-cover ring-2 ring-[#e3b25b]/40 flex-shrink-0"
          />
          <div className="leading-tight min-w-0 hidden sm:block">
            <p className="font-script text-2xl text-[#faf6ee] -mb-1 whitespace-nowrap">
              MexiHome<span className="text-[#e3b25b]">5</span>
            </p>
            <p className="text-[10px] tracking-[0.18em] italic text-[#faf6ee]/70 whitespace-nowrap">
              Alojamento Local · Algarve
            </p>
          </div>
          {/* Mobile shortened brand — only shown on slightly wider mobile */}
          <p className="hidden min-[420px]:block sm:hidden font-script text-xl text-[#faf6ee] whitespace-nowrap">
            MexiHome<span className="text-[#e3b25b]">5</span>
          </p>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) =>
            l.to ? (
              <Link
                key={l.key}
                to={l.to}
                className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#faf6ee]/85 hover:text-[#e3b25b] transition relative after:content-[''] after:absolute after:-bottom-1.5 after:left-0 after:h-[1.5px] after:w-0 hover:after:w-full after:bg-[#e3b25b] after:transition-all"
                data-testid={`nav-${l.key}`}
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.key}
                href={l.href}
                className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#faf6ee]/85 hover:text-[#e3b25b] transition relative after:content-[''] after:absolute after:-bottom-1.5 after:left-0 after:h-[1.5px] after:w-0 hover:after:w-full after:bg-[#e3b25b] after:transition-all"
                data-testid={`nav-${l.key}`}
              >
                {l.label}
              </a>
            )
          )}
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
          <LanguageToggle tone="light" />
          <a
            href={isHome ? "#reserva" : "/#reserva"}
            className="hidden md:inline-flex btn-primario py-2.5 px-5 text-[11px]"
            data-testid="cta-reservar-header"
          >
            {t.nav.cta}
          </a>
          <button
            className="lg:hidden p-2 text-[#faf6ee]"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
            data-testid="menu-toggle"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0e1a3f] border-t border-white/10 px-6 py-4 space-y-3" data-testid="mobile-menu">
          {links.map((l) =>
            l.to ? (
              <Link key={l.key} to={l.to} onClick={() => setOpen(false)} className="block py-2 text-[#faf6ee]/90 font-semibold uppercase tracking-wider text-sm">
                {l.label}
              </Link>
            ) : (
              <a key={l.key} href={l.href} onClick={() => setOpen(false)} className="block py-2 text-[#faf6ee]/90 font-semibold uppercase tracking-wider text-sm">
                {l.label}
              </a>
            )
          )}
          <a href={isHome ? "#reserva" : "/#reserva"} onClick={() => setOpen(false)} className="btn-primario w-full py-3">
            {t.nav.cta}
          </a>
        </div>
      )}
    </header>
  );
}
